import { CustomViewContentAlign } from '@src/enums/CustomViewContentAlign'
import { ApiParameterBinding, ContentAlign, Filter, FilterComponent, getApiClient, getApplicationContext, Model, Sort, SortDirection, StyleRuleContentDisplayMode, UIViewEventArgs, ViewModel, ViewModelProperty, ViewModelPropertyState, ViewModelState } from '@neos/core'
import { CustomViewStyleRuleContentDisplayMode } from '@src/enums/CustomViewStyleRuleContentDisplayMode'
import { CustomViewFilterComponent } from '@src/enums/CustomViewFilterComponent'
import { cast, compareValue, emptyGuid, getDefaultValueByType, getFirstArrayElementOrDefault, getLocalizator, Localizator, stringFormat, Task, toCamelCase, toRecord } from '@neos/shared'
import { getApplicationResourceManager } from '@neos/app'
import CustomSubUIViewUI from '@src/views/CustomSubUIViewUI/CustomSubUIViewUI'
import CustomSubUIViewPropertyUI from '@src/views/CustomSubUIViewPropertyUI/CustomSubUIViewPropertyUI'
import CustomSubUIViewEntityViewParameterUI from '@src/views/CustomSubUIViewEntityViewParameterUI/CustomSubUIViewEntityViewParameterUI'
import CustomSubUIViewStyleRuleUI from '@src/views/CustomSubUIViewStyleRuleUI/CustomSubUIViewStyleRuleUI'
import CustomUIViewCreateUI from '@src/views/CustomUIViewCreateUI/CustomUIViewCreateUI'
import CustomUIViewDataFilterUI from '@src/views/CustomUIViewDataFilterUI/CustomUIViewDataFilterUI'
import { typeEquivalenceModuleFilterOperatorFromCoreFilterOperator } from '@src/sharedMethods/typeEquivalence'
import CustomUIViewPropertyUI from '@src/views/CustomUIViewPropertyUI/CustomUIViewPropertyUI'
import CustomUIViewEntityViewParameterUI from '@src/views/CustomUIViewEntityViewParameterUI/CustomUIViewEntityViewParameterUI'
import CustomUIViewStyleRuleUI from '@src/views/CustomUIViewStyleRuleUI/CustomUIViewStyleRuleUI'
import CustomUIViewDetailView from '@src/entityViews/CustomUIViewDetailView'
import CustomUIViewGroupsUIViewModel from '@src/views/CustomUIViewGroupsUI/CustomUIViewGroupsUIViewModel'
import CustomUIViewGroupsUI from '@src/views/CustomUIViewGroupsUI/CustomUIViewGroupsUI'

/**
 * Convert content align.
 * 
 * @param contentAlign Content align.
 */
export function uiCustomizationConvertContentAlign(contentAlign: (ContentAlign | null | undefined)): CustomViewContentAlign {
  switch (contentAlign) {
  case ContentAlign.start:
  return CustomViewContentAlign.start;
  case ContentAlign.center:
  return CustomViewContentAlign.center;
  case ContentAlign.end:
  return CustomViewContentAlign.end;
  default:
  return null;
  }
}

/**
 * Convert content display mode.
 * 
 * @param contentDisplayMode Content display mode.
 */
export function uiCustomizationConvertContentDisplayMode(contentDisplayMode: (StyleRuleContentDisplayMode | null | undefined)): CustomViewStyleRuleContentDisplayMode {
  switch (contentDisplayMode) {
  case StyleRuleContentDisplayMode.iconBeforeText:
  return CustomViewStyleRuleContentDisplayMode.iconBeforeText;
  case StyleRuleContentDisplayMode.iconAfterText:
  return CustomViewStyleRuleContentDisplayMode.iconAfterText;
  case StyleRuleContentDisplayMode.icon:
  return CustomViewStyleRuleContentDisplayMode.icon;
  default:
  return null;
  }
}

/**
 * Convert filter component.
 * 
 * @param filterComponent Filter component.
 */
export function uiCustomizationConvertFilterComponent(filterComponent: FilterComponent): CustomViewFilterComponent {
  switch (filterComponent) {
  case FilterComponent.calendarDay:
  return CustomViewFilterComponent.calendarDay;
  case FilterComponent.calendarMonth:
  return CustomViewFilterComponent.calendarMonth;
  case FilterComponent.calendarYear:
  return CustomViewFilterComponent.calendarYear;
  default:
  return CustomViewFilterComponent.default;
  }
}

/**
 * Disable custom view.
 * 
 * @param viewModel View model.
 */
export function uiCustomizationDisableCustomView(viewModel: ViewModel<Model>): void {
  viewModel.contextData.set('DisableCustomView', true);
}

/**
 * Get original property value.
 * 
 * @param viewModelState View model state.
 * @param propertyName Property name.
 * @param getter Getter.
 */
export function uiCustomizationGetOriginalPropertyValue(viewModelState: ViewModelState, propertyName: string, getter: (arg1: ViewModelPropertyState) => (boolean | null | undefined)): (boolean | null | undefined) {
  let viewModelPropertyState: (ViewModelPropertyState | null | undefined) = getFirstArrayElementOrDefault(viewModelState.properties, 'ViewModelPropertyState', (p) => p.name == propertyName);
  return viewModelPropertyState != null ? getter(viewModelPropertyState) : null;
}

/**
 * Get owner view model.
 * 
 * @param viewModel View model.
 * @param subViewName Sub view name.
 */
export function uiCustomizationGetOwnerViewModel(viewModel: ViewModel<Model>, subViewName: (string | null | undefined)): ViewModel<Model> {
  let ownerViewModel = cast<ViewModel<Model>>(viewModel.viewContext.get('OwnerViewModel'));
  if (ownerViewModel == null) {
    throw new Error(getApplicationResourceManager('TaskScheduler').get('modules.UICustomization.ParentViewModelNotFound'));
  }
  if (subViewName == null) {
    return ownerViewModel;
  }
  return ownerViewModel.getSubViewModel<Model>(subViewName);
}

/**
 * Init custom sub view.
 * 
 * @param customView Custom view.
 * @param ownerViewModel Owner view model.
 * @param isCustomViewNewOrSelected Is custom view new or selected.
 */
export function uiCustomizationInitCustomSubView(customView: CustomSubUIViewUI, ownerViewModel: ViewModel<Model>, isCustomViewNewOrSelected: boolean): void {
  customView.originalOwnerViewModelState = ownerViewModel.customViewManager.getOriginalViewModelState(ownerViewModel);
  if (!isCustomViewNewOrSelected) {
    return ;
  }
  customView.dataSorts = ownerViewModel.dataSorts.length > 0 ? (ownerViewModel.dataSorts.map((s) => s.toString())).join(',') : null;
  for (let property of [...ownerViewModel.getDisplayableProperties()].sort((a,b) => compareValue(((p) => p.datagridPosition)(a),((p) => p.datagridPosition)(b)))) {
    let customViewProperty = getFirstArrayElementOrDefault(customView.propertyList, 'CustomSubUIViewPropertyUI', (c) => c.name == property.name);
    if (customViewProperty == null) {
      customViewProperty = new CustomSubUIViewPropertyUI({name : property.name});
      customView.propertyList.push(customViewProperty);
    }
    if (customViewProperty.datagridCaption == null) {
      customViewProperty.datagridCaption = toRecord(new Map<string, string>([
  [getLocalizator('TaskScheduler').currentLanguage, property.datagridCaption]
  ]));
    } else if (!Object.hasOwn(customViewProperty.datagridCaption, getLocalizator('TaskScheduler').currentLanguage)) {
      customViewProperty.datagridCaption = Localizator.withTranslation(customViewProperty.datagridCaption, getLocalizator('TaskScheduler').currentLanguage, property.datagridCaption);
    }
    customViewProperty.datagridVisible = property.datagridVisible;
    customViewProperty.datagridPosition = property.datagridPosition;
    customViewProperty.ellipsis = property.ellipsis;
    customViewProperty.datagridFrozen = property.datagridFrozen;
    customViewProperty.datagridMovable = property.datagridMovable;
    customViewProperty.datagridColumnWidth = property.datagridWidth;
    customViewProperty.horizontalAlign = uiCustomizationConvertContentAlign(property.horizontalAlign);
    if (customViewProperty.caption == null) {
      customViewProperty.caption = toRecord(new Map<string, string>([
  [getLocalizator('TaskScheduler').currentLanguage, property.caption]
  ]));
    } else if (!Object.hasOwn(customViewProperty.caption, getLocalizator('TaskScheduler').currentLanguage)) {
      customViewProperty.caption = Localizator.withTranslation(customViewProperty.caption, getLocalizator('TaskScheduler').currentLanguage, property.caption);
    }
    customViewProperty.formVisible = property.isFormVisibleExpression() ? false : property.getFormVisible(null);
    customViewProperty.readonly = property.isReadOnlyExpression() ? false : property.getReadOnly(null);
    customViewProperty.required = property.isRequiredExpression() ? false : property.getRequired(null);
    customViewProperty.tabStop = property.isTabStopExpression() ? true : property.getTabStop(null);
  }
  customView.entityViewParameterList.clear();
  for (let parameter of ownerViewModel.getEntityViewParameters()) {
    if (parameter.saveInCustomView) {
      customView.entityViewParameterList.push(new CustomSubUIViewEntityViewParameterUI({name : parameter.name, value : parameter.value != null ? JSON.stringify(parameter.value) : null}));
    }
  }
  customView.styleRuleList.clear();
  for (let userStyleRule of ownerViewModel.userStyleRules) {
    let applicationCondition: (Filter | null | undefined) = cast<(Filter | null | undefined)>((userStyleRule.applicationCondition?.clone()));
    customView.styleRuleList.push(new CustomSubUIViewStyleRuleUI({customSubUIViewId : customView.id, enabled : true, executionOrder : userStyleRule.options.executionOrder, propertyName : userStyleRule.options.propertyName, applicationCondition : (applicationCondition?.isEmpty) == false ? applicationCondition.convertToJSON() : null, applicationConditionObject : applicationCondition ?? new Filter(), lightBackgroundColor : userStyleRule.options.lightBackgroundColor, lightForegroundColor : userStyleRule.options.lightForegroundColor, darkBackgroundColor : userStyleRule.options.darkBackgroundColor, darkForegroundColor : userStyleRule.options.darkForegroundColor, iconName : userStyleRule.options.iconName, contentDisplayMode : uiCustomizationConvertContentDisplayMode(userStyleRule.options.contentDisplayMode), horizontalAlign : uiCustomizationConvertContentAlign(userStyleRule.options.horizontalAlign), bold : userStyleRule.options.bold, italic : userStyleRule.options.italic, underline : userStyleRule.options.underline, strikethrough : userStyleRule.options.strikethrough}));
  }
}

/**
 * Init custom view.
 * 
 * @param customView Custom view.
 * @param ownerViewModel Owner view model.
 * @param isCustomViewNewOrSelected Is custom view new or selected.
 */
export function uiCustomizationInitCustomView(customView: CustomUIViewCreateUI, ownerViewModel: ViewModel<Model>, isCustomViewNewOrSelected: boolean): void {
  customView.originalOwnerViewModelState = ownerViewModel.customViewManager.getOriginalViewModelState(ownerViewModel);
  if (!isCustomViewNewOrSelected) {
    for (let property of [...ownerViewModel.getFilterableProperties()].sort((a,b) => compareValue(((p) => p.filterPosition)(a),((p) => p.filterPosition)(b)))) {
      let customViewFilter = getFirstArrayElementOrDefault(customView.dataFilterList, 'CustomUIViewDataFilterUI', (f) => f.name == property.name);
      if (customViewFilter != null) {
        customViewFilter.caption = property.caption;
        if (customViewFilter.defaultOperator == null) {
          let propertyFilterDefaultOperator: (string | null | undefined) = typeEquivalenceModuleFilterOperatorFromCoreFilterOperator(property.filterDefaultOperator);
          customViewFilter.defaultOperator = propertyFilterDefaultOperator;
        }
      }
    }
    return ;
  }
  customView.uiViewName = ownerViewModel.uiViewName;
  customView.userId = getApplicationContext('TaskScheduler').getValue('UserIdentifier', getDefaultValueByType('string') as string);
  let isLookupModal = ownerViewModel.viewContext.has('neos-lookup-item');
  if (!isLookupModal) {
    let filter = ownerViewModel.dataFilter.toSimplifiedFilter();
    let quickSearchFilter: (Filter | null | undefined) = filter.find('isMatch');
    if (quickSearchFilter != null) {
      filter.remove(quickSearchFilter);
    }
    customView.dataFilter = !filter.isEmpty ? filter.convertToJSON() : null;
    customView.dataFilterObject = filter;
  } else {
    customView.dataFilter = null;
    customView.dataFilterObject = new Filter();
  }
  for (let property of [...ownerViewModel.getFilterableProperties()].sort((a,b) => compareValue(((p) => p.filterPosition)(a),((p) => p.filterPosition)(b)))) {
    let customViewFilter = getFirstArrayElementOrDefault(customView.dataFilterList, 'CustomUIViewDataFilterUI', (f) => f.name == property.name);
    let propertyFilterDefaultOperator: (string | null | undefined) = typeEquivalenceModuleFilterOperatorFromCoreFilterOperator(property.filterDefaultOperator);
    if (customViewFilter != null) {
      customViewFilter.caption = property.caption;
      customViewFilter.visible = property.filterVisible;
      customViewFilter.defaultOperator = propertyFilterDefaultOperator;
      customViewFilter.panelVisible = property.filterVisibleInPanel;
      customViewFilter.component = uiCustomizationConvertFilterComponent(property.filterComponent);
      customViewFilter.position = property.filterPosition;
    } else {
      customView.dataFilterList.push(new CustomUIViewDataFilterUI({customUIViewId : customView.id, name : property.name, caption : property.caption, visible : property.filterVisible, defaultOperator : propertyFilterDefaultOperator, panelVisible : property.filterVisibleInPanel, component : uiCustomizationConvertFilterComponent(property.filterComponent), position : property.filterPosition}));
    }
  }
  customView.dataGroups = ownerViewModel.groupDescriptions.length > 0 ? (ownerViewModel.groupDescriptions.map((s) => s)).join(',') : null;
  customView.dataSorts = ownerViewModel.dataSorts.length > 0 ? (ownerViewModel.dataSorts.map((s) => s.toString())).join(',') : null;
  for (let property of [...ownerViewModel.getDisplayableProperties()].sort((a,b) => compareValue(((p) => p.datagridPosition)(a),((p) => p.datagridPosition)(b)))) {
    let customViewProperty = getFirstArrayElementOrDefault(customView.propertyList, 'CustomUIViewPropertyUI', (c) => c.name == property.name);
    if (customViewProperty == null) {
      customViewProperty = new CustomUIViewPropertyUI({customUIViewId : customView.id, name : property.name});
      customView.propertyList.push(customViewProperty);
    }
    if (customViewProperty.datagridCaption == null) {
      customViewProperty.datagridCaption = toRecord(new Map<string, string>([
  [getLocalizator('TaskScheduler').currentLanguage, property.datagridCaption]
  ]));
    } else if (!Object.hasOwn(customViewProperty.datagridCaption, getLocalizator('TaskScheduler').currentLanguage)) {
      customViewProperty.datagridCaption = Localizator.withTranslation(customViewProperty.datagridCaption, getLocalizator('TaskScheduler').currentLanguage, property.datagridCaption);
    }
    customViewProperty.datagridVisible = property.datagridVisible;
    customViewProperty.datagridPosition = property.datagridPosition;
    customViewProperty.ellipsis = property.ellipsis;
    customViewProperty.datagridFrozen = property.datagridFrozen;
    customViewProperty.datagridMovable = property.datagridMovable;
    customViewProperty.datagridColumnWidth = property.datagridWidth;
    customViewProperty.horizontalAlign = uiCustomizationConvertContentAlign(property.horizontalAlign);
    if (customViewProperty.caption == null) {
      customViewProperty.caption = toRecord(new Map<string, string>([
  [getLocalizator('TaskScheduler').currentLanguage, property.caption]
  ]));
    } else if (!Object.hasOwn(customViewProperty.caption, getLocalizator('TaskScheduler').currentLanguage)) {
      customViewProperty.caption = Localizator.withTranslation(customViewProperty.caption, getLocalizator('TaskScheduler').currentLanguage, property.caption);
    }
    customViewProperty.formVisible = property.isFormVisibleExpression() ? false : property.getFormVisible(null);
    customViewProperty.readonly = property.isReadOnlyExpression() ? false : property.getReadOnly(null);
    customViewProperty.required = property.isRequiredExpression() ? false : property.getRequired(null);
    customViewProperty.tabStop = property.isTabStopExpression() ? true : property.getTabStop(null);
  }
  customView.entityViewParameterList.clear();
  for (let parameter of ownerViewModel.getEntityViewParameters()) {
    if (parameter.saveInCustomView) {
      customView.entityViewParameterList.push(new CustomUIViewEntityViewParameterUI({name : parameter.name, value : parameter.value != null ? JSON.stringify(parameter.value) : null}));
    }
  }
  customView.styleRuleList.clear();
  for (let userStyleRule of ownerViewModel.userStyleRules) {
    let applicationCondition: (Filter | null | undefined) = cast<(Filter | null | undefined)>((userStyleRule.applicationCondition?.clone()));
    customView.styleRuleList.push(new CustomUIViewStyleRuleUI({customUIViewId : customView.id, enabled : true, executionOrder : userStyleRule.options.executionOrder, propertyName : userStyleRule.options.propertyName, applicationCondition : (applicationCondition?.isEmpty) == false ? applicationCondition.convertToJSON() : null, applicationConditionObject : applicationCondition ?? new Filter(), lightBackgroundColor : userStyleRule.options.lightBackgroundColor, lightForegroundColor : userStyleRule.options.lightForegroundColor, darkBackgroundColor : userStyleRule.options.darkBackgroundColor, darkForegroundColor : userStyleRule.options.darkForegroundColor, iconName : userStyleRule.options.iconName, contentDisplayMode : uiCustomizationConvertContentDisplayMode(userStyleRule.options.contentDisplayMode), horizontalAlign : uiCustomizationConvertContentAlign(userStyleRule.options.horizontalAlign), bold : userStyleRule.options.bold, italic : userStyleRule.options.italic, underline : userStyleRule.options.underline, strikethrough : userStyleRule.options.strikethrough}));
  }
}

/**
 * Is custom view disabled.
 * 
 * @param viewModel View model.
 */
export function uiCustomizationIsCustomViewDisabled(viewModel: ViewModel<Model>): boolean {
  return cast<(boolean | null | undefined)>(viewModel.contextData.get('DisableCustomView')) == true;
}

/**
 * On layout updated async.
 * 
 * @param args Args.
 */
export async function uiCustomizationOnLayoutUpdatedAsync(args: UIViewEventArgs): Promise<void> {
  let mainViewModel = args.viewModel;
  while (!mainViewModel.isMain) {
    mainViewModel = mainViewModel.parentViewModel;
  }
  if (uiCustomizationIsCustomViewDisabled(mainViewModel)) {
    return ;
  }
  let autoCustomView = new CustomUIViewCreateUI();
  autoCustomView.id = emptyGuid;
  autoCustomView.name = { [getLocalizator('TaskScheduler').defaultLanguage]: 'Default' };
  uiCustomizationInitCustomView(autoCustomView, mainViewModel, true);
  for (let relation of mainViewModel.relationships) {
    let subViewModel = mainViewModel.getSubViewModel<Model>(relation.path);
    if (uiCustomizationIsCustomViewDisabled(subViewModel)) {
      continue;
    }
    let autoCustomSubView = new CustomSubUIViewUI({parentCollectionName : relation.path});
    uiCustomizationInitCustomSubView(autoCustomSubView, subViewModel, true);
    autoCustomView.subUIViewList.push(autoCustomSubView);
  }
  try {
    mainViewModel.currentCustomViewId = await getApiClient('TaskScheduler').callPostServerMethodAsync<string>('$neos/save-auto-custom-view', ApiParameterBinding.bodyProperty, { autoCustomView: cast<CustomUIViewDetailView>(autoCustomView.toApiRequestData(false)) });
  }
  catch(ex) {
    if (ex instanceof Error) {
      console.log(stringFormat('Error while saving the auto custom view: {0}', ex));
    } else {
      throw ex;
    }
  }
}

/**
 * Refresh groups ui view.
 * 
 * @param viewModel View model.
 * @param tabIdentifier Tab identifier.
 * @param parentCollectionName Parent collection name.
 * @param dataGroups Data groups.
 * @param setValueCallback Set value callback.
 */
export function uiCustomizationRefreshGroupsUiView(viewModel: ViewModel<Model>, tabIdentifier: string, parentCollectionName: (string | null | undefined), dataGroups: (string | null | undefined), setValueCallback: (args1:string) => void): void {
  let groupsViewModel = cast<CustomUIViewGroupsUIViewModel>(viewModel.findViewModelById<CustomUIViewGroupsUI>(tabIdentifier));
  if (groupsViewModel != null) {
    let ownerViewModel = uiCustomizationGetOwnerViewModel(viewModel, parentCollectionName);
    let sortablesPropertiesList: [string, string][] = [];
    for (let property of [...ownerViewModel.getDisplayableProperties().filter((p) => p.serverSideSortable)].sort((a,b) => compareValue(((p) => p.name)(a),((p) => p.name)(b)))) {
      let tuple = cast<[string, string]>([property.name, property.datagridCaption]);
      sortablesPropertiesList.push(tuple);
    }
    groupsViewModel.methods.refreshThisUiView(sortablesPropertiesList, dataGroups ?? '', setValueCallback);
  }
}

/**
 * Set property list sort.
 * 
 * @param viewModel View model.
 * @param ownerViewModel Owner view model.
 */
export function uiCustomizationSetPropertyListSort(viewModel: ViewModel<Model>, ownerViewModel: ViewModel<Model>): void {
  if (ownerViewModel.monoRecord) {
    viewModel.setSort(toCamelCase('Name'), SortDirection.asc);
  } else {
    viewModel.setSort(toCamelCase('DatagridPosition'), SortDirection.asc);
  }
}
